import React from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import MainLayout from './components/Layout/MainLayout';
import LoginPage from './pages/LoginPage';

// Import all pages
import HomePage from './pages/HomePage';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import AppointmentsPage from './pages/AppointmentsPage';
import AppointmentDetailPage from './pages/AppointmentDetailPage';
import TreatmentRecordsPage from './pages/TreatmentRecordsPage';
import TreatmentRecordDetailPage from './pages/TreatmentRecordDetailPage';
import TreatmentsPage from './pages/TreatmentsPage';
import TreatmentDetailPage from './pages/TreatmentDetailPage';
import PractitionersPage from './pages/PractitionersPage';
import PractitionerDetailPage from './pages/PractitionerDetailPage';
import VenuesPage from './pages/VenuesPage';
import VenueDetailPage from './pages/VenueDetailPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import NotFoundPage from './pages/NotFoundPage';
import UsersPage from './pages/UsersPage';

/**
 * Protected Route component that redirects to login if not authenticated
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // Show loading indicator while checking auth status
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If authenticated, render children
  return children;
};

/**
 * Role-based protected route
 */
const RoleProtectedRoute = ({ requiredRole, children }) => {
  const { hasRole, loading } = useAuth();

  // Show loading indicator while checking auth status
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  // Redirect to home if not authorized for this role
  if (!hasRole(requiredRole)) {
    return <Navigate to="/" replace />;
  }

  // If authorized, render children
  return children;
};

/**
 * Layout wrapper for protected routes
 */
const ProtectedLayout = ({ children }) => {
  return (
    <ProtectedRoute>
      <MainLayout>{children}</MainLayout>
    </ProtectedRoute>
  );
};

/**
 * Router configuration
 */
const router = createBrowserRouter([
  // Public routes
  {
    path: '/login',
    element: <LoginPage />
  },
  
  // Protected routes
  {
    path: '/',
    element: (
      <ProtectedLayout>
        <HomePage />
      </ProtectedLayout>
    )
  },
  
  // Customer routes
  {
    path: '/customers',
    element: (
      <ProtectedLayout>
        <CustomersPage />
      </ProtectedLayout>
    )
  },
  {
    path: '/customers/new',
    element: (
      <ProtectedLayout>
        <CustomersPage />
      </ProtectedLayout>
    )
  },
  {
    path: '/customers/:id',
    element: (
      <ProtectedLayout>
        <CustomerDetailPage />
      </ProtectedLayout>
    )
  },
  
  // Appointment routes
  {
    path: '/appointments',
    element: (
      <ProtectedLayout>
        <AppointmentsPage />
      </ProtectedLayout>
    )
  },
  {
    path: '/appointments/new',
    element: (
      <ProtectedLayout>
        <AppointmentsPage />
      </ProtectedLayout>
    )
  },
  {
    path: '/appointments/:id',
    element: (
      <ProtectedLayout>
        <AppointmentDetailPage />
      </ProtectedLayout>
    )
  },
  
  // Treatment record routes
  {
    path: '/treatment-records',
    element: (
      <ProtectedLayout>
        <TreatmentRecordsPage />
      </ProtectedLayout>
    )
  },
  {
    path: '/treatment-records/new',
    element: (
      <ProtectedLayout>
        <TreatmentRecordsPage />
      </ProtectedLayout>
    )
  },
  {
    path: '/treatment-records/:id',
    element: (
      <ProtectedLayout>
        <TreatmentRecordDetailPage />
      </ProtectedLayout>
    )
  },
  
  // Treatment routes
  {
    path: '/treatments',
    element: (
      <ProtectedLayout>
        <TreatmentsPage />
      </ProtectedLayout>
    )
  },
  {
    path: '/treatments/new',
    element: (
      <ProtectedLayout>
        <TreatmentsPage />
      </ProtectedLayout>
    )
  },
  {
    path: '/treatments/:id',
    element: (
      <ProtectedLayout>
        <TreatmentDetailPage />
      </ProtectedLayout>
    )
  },
  
  // Practitioner routes
  {
    path: '/practitioners',
    element: (
      <ProtectedLayout>
        <PractitionersPage />
      </ProtectedLayout>
    )
  },
  {
    path: '/practitioners/new',
    element: (
      <ProtectedLayout>
        <PractitionersPage />
      </ProtectedLayout>
    )
  },
  {
    path: '/practitioners/:id',
    element: (
      <ProtectedLayout>
        <PractitionerDetailPage />
      </ProtectedLayout>
    )
  },
  
  // Venue routes
  {
    path: '/venues',
    element: (
      <ProtectedLayout>
        <VenuesPage />
      </ProtectedLayout>
    )
  },
  {
    path: '/venues/new',
    element: (
      <ProtectedLayout>
        <VenuesPage />
      </ProtectedLayout>
    )
  },
  {
    path: '/venues/:id',
    element: (
      <ProtectedLayout>
        <VenueDetailPage />
      </ProtectedLayout>
    )
  },
  
  // User profile and settings
  {
    path: '/profile',
    element: (
      <ProtectedLayout>
        <ProfilePage />
      </ProtectedLayout>
    )
  },
  {
    path: '/settings',
    element: (
      <ProtectedLayout>
        <SettingsPage />
      </ProtectedLayout>
    )
  },
  
  // Users (员工管理) routes
  {
    path: '/users',
    element: (
      <ProtectedLayout>
        <UsersPage />
      </ProtectedLayout>
    )
  },
  
  // 404 - Not Found
  {
    path: '*',
    element: <NotFoundPage />
  }
]);

/**
 * Router component with authentication provider
 */
const AppRouter = () => {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
};

export default AppRouter;